import {
  type ComponentType,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SVGProps,
} from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../lib/utils.js";
import { Card } from "../molecules/index.js";

export interface WidgetCardProps extends HTMLAttributes<HTMLElement> {
  /** Card title — shown below the eyebrow, prominent. */
  title?: string;
  /** Optional eyebrow shown ABOVE the title (uppercase, muted). Pair with `icon`. */
  context?: string;
  /** Short explanation shown directly below the title. */
  description?: string;
  /** Icon rendered next to `context`. Compatible with lucide-react. */
  icon?: ComponentType<
    Pick<SVGProps<SVGSVGElement>, "className" | "aria-hidden">
  >;
  /** Optional footer link label. Rendered as cyan "label ›" in a tinted footer. */
  footerLink?: string;
  /** Right-aligned header controls — filters, action icons, etc. */
  headerActions?: ReactNode;
  /** Suppress the default 20px body padding (caller takes over). */
  noPadding?: boolean;
  /** Additional classes for the padded body region. */
  bodyClassName?: string;
  /** Optional 1px decorative purple hairline at the top edge. A widget card is
   *  content, so it never carries a module identity hue (ADR-0092). */
  accent?: "purple" | null;
}

/**
 * Generic frame for any home-dashboard or entity-dashboard widget.
 *
 * Visual language mirrors the playbook-ui reference library: hairline-lit
 * surface (`pb-hairline`), `bg-pb-card`, `border-pb-border`, `rounded-xl`,
 * `shadow-sm`. Context line is uppercase 10px tracking-wider muted; title is
 * `text-base font-medium text-pb-text leading-tight`. Footer link is secondary text
 * with a chevron on `bg-pb-card-elevated` separated by a top border.
 *
 * Used for dashboard rendering (Home, KPI Tracker, future Entity Dashboard).
 * Canvas rendering uses its own ChromeShell inside `CanvasWidget` instead.
 */
export function WidgetCard({
  title,
  context,
  description,
  icon: Icon,
  footerLink,
  headerActions,
  noPadding,
  bodyClassName,
  accent,
  className,
  children,
  ...rest
}: WidgetCardProps): ReactElement {
  return (
    <Card
      as="section"
      className={cn(
        "pb-motion-content-enter relative flex h-full flex-col",
        className,
      )}
      {...rest}
    >
      {accent === "purple" ? (
        <div
          aria-hidden
          className="absolute top-0 start-0 end-0 h-px bg-pb-purple opacity-50"
        />
      ) : null}
      <div
        // The body carries today's text colour: Card sets none, and a colour
        // class on the Card itself would be an override on a DS part.
        className={cn(
          "flex-1 min-h-0 text-pb-text",
          noPadding ? "" : "p-5",
          bodyClassName,
        )}
      >
        {context && Icon ? (
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-pb-text-muted uppercase tracking-wider text-dense-2 font-semibold">
              <Icon className="w-3.5 h-3.5" />
              <span>{context}</span>
            </div>
            {headerActions ? (
              <div className="shrink-0 flex items-center gap-1">
                {headerActions}
              </div>
            ) : null}
          </div>
        ) : headerActions ? (
          <div className="mb-3 flex items-center justify-end gap-1">
            {headerActions}
          </div>
        ) : null}
        {title ? (
          description ? (
            <div className="mb-4 flex min-w-0 items-start gap-3">
              {Icon && !context ? (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-pb-card-elevated text-pb-cyan-text">
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
              ) : null}
              <div className="min-w-0">
                <h3 className="text-base font-medium leading-tight text-pb-text">
                  {title}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-pb-text-muted">
                  {description}
                </p>
              </div>
            </div>
          ) : (
            <h3 className="mb-4 text-base font-medium text-pb-text leading-tight">
              {title}
            </h3>
          )
        ) : null}
        {children}
      </div>
      {footerLink ? (
        <div className="border-t border-pb-border/70 bg-pb-card-elevated px-5 py-3">
          {/* Plain text: the footer has no target to navigate to (it was a
              button with no action). */}
          <span className="inline-flex items-center text-sm font-medium text-pb-text-secondary">
            {footerLink} <ChevronRight className="ms-1 h-4 w-4" aria-hidden />
          </span>
        </div>
      ) : null}
    </Card>
  );
}
